/**
 * The photo grid.
 *
 * Virtualised by row, so a group with 50 000 photos renders about 40 DOM nodes.
 * Rows are built from month sections: a header row, then as many photo rows as
 * that month needs. Because the row list is derived from loaded items only, new
 * pages extend it without shifting anything already on screen.
 *
 * Tiles reserve their space with a fixed aspect ratio, so nothing reflows as
 * images decode — the single biggest cause of a gallery feeling janky.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { GalleryItem, GallerySection } from "./useGallery.js";
import { Skeleton } from "../../components/ui/Skeleton.js";
import { useDecryptedUrl, type MediaRef } from "../../lib/useDecryptedUrl.js";
import "./PhotoGrid.css";

const HEADER_HEIGHT = 44;
const MAX_COLUMNS = 12;

/**
 * Tile size per density.
 *
 * Three steps rather than a slider: a continuous control invites fiddling and
 * produces column counts that look accidental. These are the sizes that give a
 * tidy grid at common widths.
 */
export type Density = "compact" | "comfortable" | "large";

const MIN_TILE: Record<Density, number> = {
  compact: 104,
  comfortable: 160,
  large: 260,
};

type Row =
  | { type: "header"; key: string; label: string; count: number }
  | { type: "tiles"; key: string; items: GalleryItem[] };

export interface PhotoGridProps {
  sections: GallerySection[];
  /** Signed URL plus sealed key, per file. The tile does the decrypting. */
  thumbRefs: Map<string, MediaRef>;
  /**
   * Files with no thumbnail, and why — as opposed to files not asked about yet.
   *
   * The reason is shown on the tile. Four different failures used to render as
   * the same grey box, which is what made every one of them impossible to
   * diagnose from a screenshot.
   */
  thumbsUnavailable: ReadonlyMap<string, string>;
  /** Called after a tile rebuilds its own preview. */
  onRebuilt: () => void;
  groupId: string;
  /**
   * Which of these are reachable through a live share link.
   *
   * A list of links does not answer “is this photograph outside the group”,
   * and that is the question people actually have. Marked on the tile so
   * the answer is where the photograph is.
   */
  sharedFileIds: ReadonlySet<string>;
  selected: ReadonlySet<string>;
  selectionMode: boolean;
  /**
   * Whether tiles can be dragged onto somebody in the People strip.
   *
   * Off unless that strip is open. A tile that is always draggable turns every
   * slightly-slow click into a drag, and there would be nothing to drop it on.
   */
  assignable?: boolean;
  onOpen: (fileId: string) => void;
  onToggleSelect: (
    fileId: string,
    event: React.MouseEvent | React.KeyboardEvent,
  ) => void;
  onLoadMore: () => void;
  hasMore: boolean;
  isLoadingMore: boolean;
  density: Density;
}

export function PhotoGrid({
  sections,
  thumbRefs,
  thumbsUnavailable,
  onRebuilt,
  sharedFileIds,
  groupId,
  selected,
  selectionMode,
  assignable,
  onOpen,
  onToggleSelect,
  onLoadMore,
  hasMore,
  isLoadingMore,
  density,
}: PhotoGridProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  // ResizeObserver rather than a window listener: the grid also changes width
  // when the context rail opens, which a window resize event never fires for.
  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;

    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const gap = density === "compact" ? 4 : 8;
  const minTile = MIN_TILE[density];
  /*
   * One column is allowed, and that is the whole point of the largest step.
   *
   * The floor used to be two, which on a phone made "large" and "medium"
   * identical: 375px of screen fits two 160px tiles and cannot fit two 260px
   * ones, so the clamp quietly turned the biggest setting back into the middle
   * one. Three buttons, two outcomes — the control looked broken because on a
   * phone it was.
   */
  const columns = Math.max(
    1,
    Math.min(MAX_COLUMNS, Math.floor((width + gap) / (minTile + gap)) || 1),
  );
  const tileSize =
    width > 0 ? Math.floor((width - gap * (columns - 1)) / columns) : minTile;

  const rows = useMemo<Row[]>(() => {
    const output: Row[] = [];
    for (const section of sections) {
      output.push({
        type: "header",
        key: `h:${section.key}`,
        label: section.label,
        count: section.items.length,
      });
      for (let i = 0; i < section.items.length; i += columns) {
        output.push({
          type: "tiles",
          key: `r:${section.key}:${i}`,
          items: section.items.slice(i, i + columns),
        });
      }
    }
    return output;
  }, [sections, columns]);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) =>
      rows[index]?.type === "header" ? HEADER_HEIGHT : tileSize + gap,
    // Three screens of overscan: enough that a fast flick never shows holes,
    // few enough that we are not signing URLs for photos nobody will see.
    overscan: 6,
    getItemKey: (index) => rows[index]?.key ?? index,
  });

  // Infinite scroll driven by the virtualiser rather than a sentinel element,
  // so it also fires when the user drags the scrollbar straight to the bottom.
  const virtualRows = virtualizer.getVirtualItems();
  const lastVisible = virtualRows.at(-1)?.index ?? 0;

  useEffect(() => {
    if (hasMore && !isLoadingMore && lastVisible >= rows.length - 4) {
      onLoadMore();
    }
  }, [hasMore, isLoadingMore, lastVisible, rows.length, onLoadMore]);

  const handleTileKey = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>, item: GalleryItem) => {
      if (event.key === "Enter") {
        event.preventDefault();
        onOpen(item.id);
      } else if (event.key === " ") {
        event.preventDefault();
        onToggleSelect(item.id, event);
      }
    },
    [onOpen, onToggleSelect],
  );

  return (
    <div className="fc-grid-scroll" ref={scrollRef} tabIndex={-1}>
      <div
        className="fc-grid-canvas"
        style={{ height: virtualizer.getTotalSize() }}
      >
        {virtualRows.map((virtualRow) => {
          const row = rows[virtualRow.index];
          if (!row) return null;

          return (
            <div
              key={row.key}
              className="fc-grid-row"
              style={{ transform: `translateY(${virtualRow.start}px)` }}
              ref={virtualizer.measureElement}
              data-index={virtualRow.index}
            >
              {row.type === "header" ? (
                <h2 className="fc-grid-header">
                  {row.label}
                  <span className="fc-grid-header__count fc-tabular">
                    {row.count}
                  </span>
                </h2>
              ) : (
                <div
                  className="fc-grid-tiles"
                  style={{
                    gap,
                    gridTemplateColumns: `repeat(${columns}, ${tileSize}px)`,
                    /*
                     * The row's own height is what separates it from the next
                     * one.
                     *
                     * `estimateSize` returns `tileSize + gap`, but the row is
                     * handed to `measureElement`, so the measured height —
                     * exactly `tileSize` — wins and the gap between rows
                     * collapses to zero. Tiles then touch vertically while
                     * sitting `gap` apart horizontally. Carrying the gap as
                     * padding makes the measurement agree with the estimate.
                     */
                    paddingBlockEnd: gap,
                  }}
                >
                  {row.items.map((item) => (
                    <Tile
                      key={item.id}
                      item={item}
                      size={tileSize}
                      source={thumbRefs.get(item.id)}
                      noThumbnail={thumbsUnavailable.has(item.id)}
                      noThumbnailReason={thumbsUnavailable.get(item.id)}
                      onRebuilt={onRebuilt}
                      groupId={groupId}
                      shared={sharedFileIds.has(item.id)}
                      selected={selected.has(item.id)}
                      selectionMode={selectionMode}
                      onOpen={onOpen}
                      onToggleSelect={onToggleSelect}
                      onKeyDown={handleTileKey}
                      assignable={assignable}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {isLoadingMore && (
        <div className="fc-grid-loading" aria-live="polite">
          Loading more photos…
        </div>
      )}
    </div>
  );
}

interface TileProps {
  item: GalleryItem;
  size: number;
  /** Whether this tile can be dragged onto somebody in the People strip. */
  assignable?: boolean;
  /** True when a live share link reaches this photograph. */
  shared: boolean;
  source: MediaRef | undefined;
  /** True once we know there is no thumbnail, rather than not knowing yet. */
  noThumbnail: boolean;
  /** A short code naming the failure, shown on the tile so it can be quoted. */
  noThumbnailReason?: string;
  /** Drop the cached signing answers so a rebuilt thumbnail is actually fetched. */
  onRebuilt: () => void;
  groupId: string;
  selected: boolean;
  selectionMode: boolean;
  onOpen: (fileId: string) => void;
  onToggleSelect: (
    fileId: string,
    event: React.MouseEvent | React.KeyboardEvent,
  ) => void;
  onKeyDown: (
    event: React.KeyboardEvent<HTMLDivElement>,
    item: GalleryItem,
  ) => void;
}

function Tile({
  item,
  size,
  source,
  noThumbnail,
  noThumbnailReason,
  onRebuilt,
  groupId,
  selected,
  selectionMode,
  shared,
  onOpen,
  onToggleSelect,
  onKeyDown,
  assignable,
}: TileProps) {
  const [loaded, setLoaded] = useState(false);
  /** The element reported an error decoding what it was given. */
  const [broken, setBroken] = useState(false);
  const [rebuildState, setRebuildState] = useState<
    "idle" | "working" | "failed"
  >("idle");
  /*
   * Why it failed, kept rather than discarded.
   *
   * This used to be a bare `catch {}`, so a rebuild that could not work said
   * "Rebuild failed" and nothing else — to the person pressing it, and to
   * anyone trying to find out why. The reasons are genuinely different and
   * point in different directions: a missing original, a locked keyring, an
   * image this browser cannot decode, storage refusing the request.
   */
  const [rebuildError, setRebuildError] = useState<string | null>(null);

  /**
   * Make this photograph's thumbnails again, here, from its original.
   *
   * Only the browser can: the derivatives are made from plaintext and the
   * server has never held the key. On success the gallery's signing queries are
   * dropped so the new thumbnail is fetched rather than the remembered absence.
   */
  const runRebuild = useCallback(async () => {
    setRebuildState("working");
    try {
      const { rebuildPreview } = await import("../../lib/rebuildPreview.js");
      await rebuildPreview(groupId, item.id);
      onRebuilt();
    } catch (error) {
      setRebuildState("failed");
      setRebuildError(
        error instanceof Error
          ? error.message
          : "Something went wrong rebuilding this.",
      );
    }
  }, [groupId, item.id, onRebuilt]);
  const pressTimer = useRef<number | null>(null);

  /*
   * Fetched and opened here rather than upstream, because only tiles that
   * actually mount should cost a request — and with the grid virtualised, only
   * the ones on screen ever do. This is what `loading="lazy"` used to buy,
   * back when the browser was the one fetching.
   */
  const decrypted = useDecryptedUrl(source, groupId);
  const url = decrypted.url;

  // Long-press selects on touch, matching the platform convention. Cancelled by
  // any movement so it never fights a scroll gesture.
  const startPress = useCallback(
    (event: React.PointerEvent) => {
      if (event.pointerType !== "touch") return;
      pressTimer.current = window.setTimeout(() => {
        onToggleSelect(item.id, event as unknown as React.MouseEvent);
        navigator.vibrate?.(8);
      }, 450);
    },
    [item.id, onToggleSelect],
  );

  const cancelPress = useCallback(() => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  }, []);

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      // Once anything is selected, a plain click extends the selection rather
      // than opening — the behaviour every photo app shares.
      if (selectionMode || event.metaKey || event.ctrlKey || event.shiftKey) {
        event.preventDefault();
        onToggleSelect(item.id, event);
        return;
      }
      onOpen(item.id);
    },
    [selectionMode, item.id, onOpen, onToggleSelect],
  );

  /*
   * Three different unhappy endings, which used to be one.
   *
   * "Couldn't process" is what the server says about a file it could not
   * decode. It is the wrong sentence for a photograph that is perfectly fine
   * and merely closed — telling somebody their picture is broken when the
   * actual problem is that they refreshed the page would send them looking for
   * a fault that is not there.
   */
  const locked = decrypted.locked;
  /*
   * Storage refused the request — almost always a bucket that does not list
   * this site as an allowed origin. Separated from every other failure because
   * it is the only one where the photograph is provably fine and the button
   * beneath it cannot possibly help.
   */
  const blocked = decrypted.blocked;
  const unreadable = (decrypted.failed || broken) && !locked && !blocked;
  const failed = item.status === "failed";
  const processing =
    item.status === "processing" || item.status === "uploading";

  /*
   * The server has answered and there is no thumbnail for this file.
   *
   * A fourth ending, and the one that had no words at all. "No URL yet" and
   * "no URL, ever" reached the tile as the same absent map entry, so a file the
   * server had already given up on drew the same skeleton as one still being
   * signed — and a skeleton means "loading". A whole group could sit like that
   * indefinitely with nothing said anywhere.
   *
   * `processing` still wins: a file the worker has not finished legitimately
   * has no thumbnail, and that state has its own, more accurate label.
   */
  const missingThumbnail =
    noThumbnail && !processing && !failed && !locked && !blocked;

  return (
    <div
      className="fc-tile"
      data-selected={selected || undefined}
      data-state={item.status}
      data-kind={item.kind}
      style={{ width: size, height: size }}
      draggable={assignable || undefined}
      onDragStart={
        assignable
          ? (event) => {
              /*
               * A private type, so this is only droppable where it means
               * something. `text/plain` would also let the photograph be
               * dropped into a text field, which would paste a file id.
               */
              event.dataTransfer.setData("text/fc-photo", item.id);
              event.dataTransfer.effectAllowed = "link";
            }
          : undefined
      }
      role="button"
      tabIndex={0}
      aria-label={`${item.name}, ${new Date(item.takenAt).toLocaleDateString()}${
        selected ? ", selected" : ""
      }`}
      aria-pressed={selectionMode ? selected : undefined}
      onClick={handleClick}
      onKeyDown={(event) => onKeyDown(event, item)}
      onPointerDown={startPress}
      onPointerUp={cancelPress}
      onPointerMove={cancelPress}
      onPointerCancel={cancelPress}
      onContextMenu={(event) => {
        event.preventDefault();
        onToggleSelect(item.id, event);
      }}
    >
      {url && !failed && !locked && (
        <img
          className="fc-tile__image"
          src={url}
          alt=""
          width={size}
          height={size}
          /*
           * No `loading="lazy"`, and its presence here was a bug rather than an
           * optimisation.
           *
           * `useDecryptedUrl` already says why: the grid is virtualised, so only
           * tiles on screen ever mount and only those ever fetch, and the
           * attribute "stops helping once the browser is not the one fetching".
           * By the time this element exists the bytes have been downloaded and
           * decrypted and `src` is a `blob:` URL pointing at memory — there is
           * nothing left to defer.
           *
           * What it did instead was add a way to fail silently. Lazy loading is
           * driven by an intersection check the browser only re-evaluates on
           * scroll, resize or a visibility change; a tile that arrives already
           * in place — coming back to a group, or a route swapping content
           * beneath a still viewport — can be recorded as "not near the
           * viewport" and never looked at again. The element then sits at
           * `naturalWidth: 0`, `complete: false`, for ever, with no error to
           * report, which the tile renders as a permanent skeleton: a white
           * square that a reload does not fix, on a photograph whose bytes are
           * already in memory and which opens perfectly in the viewer.
           */
          decoding="async"
          draggable={false}
          onLoad={() => setLoaded(true)}
          /*
           * And if it genuinely cannot be decoded, say so. Without this the
           * failure is indistinguishable from still loading, which is the state
           * the bug above was hiding in.
           */
          onError={() => setBroken(true)}
          data-loaded={loaded || undefined}
        />
      )}

      {!loaded && !failed && !locked && !unreadable && !missingThumbnail && (
        <Skeleton className="fc-tile__skeleton" />
      )}

      {missingThumbnail && (
        <div className="fc-tile__error" data-reason="no-thumbnail">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 5h16v14H4zM4 16l4.5-4.5 3 3L15 11l5 5" />
          </svg>
          {/* The file is intact and opens; only its thumbnail is absent. */}
          <span title={rebuildError ?? undefined}>
            {rebuildState === "failed" ? "Rebuild failed" : "No preview"}
          </span>
          {rebuildError && <span className="fc-tile__why">{rebuildError}</span>}

          {/*
            The code, in the corner, selectable.

            Four different failures rendered as this same grey box — no
            derivative was ever made, the row was deleted, signing threw, the
            request was refused — and nothing on screen told them apart. That
            is why every one of these was diagnosed by guesswork, badly and
            repeatedly. A short code that can be read off a screenshot or
            copied out of the page ends that.
          */}
          {noThumbnailReason && (
            <span
              className="fc-tile__code"
              title="What went wrong. Copy this if you report it."
              onClick={(event) => event.stopPropagation()}
            >
              {noThumbnailReason}
            </span>
          )}
          {/*
            Offered rather than done. Rebuilding downloads the full-resolution
            original to make a 480px square out of it, so a gallery that healed
            itself would pull hundreds of megabytes through somebody's phone
            without being asked. One file, one tap, and only for photographs —
            a video's poster is the server's to make and it can.
          */}
          {item.kind === "photo" && (
            <button
              type="button"
              className="fc-tile__rebuild"
              disabled={rebuildState === "working"}
              onClick={(event) => {
                event.stopPropagation();
                void runRebuild();
              }}
            >
              {rebuildState === "working" ? "Rebuilding…" : "Rebuild"}
            </button>
          )}
        </div>
      )}

      {/*
        A processing tile used to be a flat black square with a tiny caption —
        indistinguishable from a broken image. It now animates for as long as
        the work is actually running, and says which stage it is at, so the
        wait reads as progress rather than as a failure.
      */}
      {processing && (
        <div className="fc-tile__working" aria-live="polite">
          <span className="fc-tile__pulse" aria-hidden="true" />
          <span className="fc-tile__working-label">
            {item.status === "uploading" ? "Uploading" : "Processing"}
          </span>
        </div>
      )}

      {failed && (
        <div className="fc-tile__error">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 8v5M12 16.5h.01M10.3 3.9 2.4 17.2A2 2 0 0 0 4.1 20h15.8a2 2 0 0 0 1.7-2.8L13.7 3.9a2 2 0 0 0-3.4 0z" />
          </svg>
          <span>Couldn&rsquo;t process</span>
        </div>
      )}

      {locked && (
        <div className="fc-tile__error" data-reason="locked">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 10.5h12v9H6zM8.5 10.5V7a3.5 3.5 0 1 1 7 0v3.5" />
          </svg>
          <span>Locked</span>
        </div>
      )}

      {blocked && (
        <div className="fc-tile__error" data-reason="blocked">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18M12 3a9 9 0 1 1 0 18 9 9 0 0 1 0-18z" />
          </svg>
          <span>Storage blocked</span>
        </div>
      )}

      {unreadable && !failed && (
        <div className="fc-tile__error">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 8v5M12 16.5h.01M10.3 3.9 2.4 17.2A2 2 0 0 0 4.1 20h15.8a2 2 0 0 0 1.7-2.8L13.7 3.9a2 2 0 0 0-3.4 0z" />
          </svg>
          <span>Couldn&rsquo;t open</span>
        </div>
      )}

      {item.kind === "video" && (
        <div className="fc-tile__duration fc-tabular">
          {formatDuration(item.durationMs)}
        </div>
      )}

      {/*
        Quiet on purpose: an outline, in the corner opposite the tick, with
        no colour of its own. Sharing is not an error and not an achievement
        — it is a fact worth being able to spot while scrolling, and a badge
        loud enough to notice one photograph is loud enough to ruin fifty.
      */}
      {shared && (
        <svg
          className="fc-tile__shared"
          viewBox="0 0 24 24"
          role="img"
          aria-label="Shared with a link"
        >
          <circle cx="18" cy="5.5" r="2.6" />
          <circle cx="6" cy="12" r="2.6" />
          <circle cx="18" cy="18.5" r="2.6" />
          <path d="M8.3 10.8 15.7 6.8M8.3 13.2l7.4 4" />
        </svg>
      )}

      {item.favorited && (
        <svg
          className="fc-tile__favorite"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path d="M12 21s-8-4.9-8-10.4A4.6 4.6 0 0 1 12 7a4.6 4.6 0 0 1 8 3.6C20 16.1 12 21 12 21z" />
        </svg>
      )}

      <button
        type="button"
        className="fc-tile__check"
        aria-label={selected ? `Deselect ${item.name}` : `Select ${item.name}`}
        aria-pressed={selected}
        onClick={(event) => {
          event.stopPropagation();
          onToggleSelect(item.id, event);
        }}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M5 12.5l4.5 4.5L19 7.5" />
        </svg>
      </button>
    </div>
  );
}

function formatDuration(ms: number | null): string {
  if (!ms) return "";
  const total = Math.round(ms / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}
